import React, { useCallback, useEffect, useState, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Column } from 'devextreme-react/data-grid';

import * as Actions from '../../../redux/multimediaActions';
import DataGrid from '../../../../../components/DataGrid';
import * as Selectors from '../../../redux/multimediaSelectors';
import useMainProgressContext from '../../../../../components/progress/MainProgress/context/MainProgressContext';
import useDataGridProgressContext from '../../../../../components/progress/DataGridProgress/context/DataGridProgressContext';
import { API_REQUEST_STATE } from '../../../../../core/enums/apiRequestState';
import { DataGridCellRender } from '../../../../../components/DataGrid/model/dataGridCellRenderModel';
import { MultimediaGetResponse } from '../../../model/multimediaModel';
import DataGridLinkTemplate from '../../../../../components/DataGrid/template/DataGridLinkTemplate';
import { ROUTES } from '../../../../../core/router/routes';
import DataGridActionTemplate from '../../../../../components/DataGrid/template/DataGridActionTemplate';
import DataGridImageTemplate from '../../../../../components/DataGrid/template/DataGridImageTemplate';
import SnackbarSettings from '../../../../../components/Snackbar/SnackbarSettings';
import ActionConfirmationDialog from '../../../../../components/ConfirmationDialog';

import './MultimediaDataGrid.scss';

const linkRender = (cellData: DataGridCellRender<MultimediaGetResponse>, name: string) => {
  return <DataGridLinkTemplate to={ROUTES.MULTIMEDIA.EDIT} value={name} id={cellData.data.id!} />;
};

interface MultimediaDataGridProps {
  handleAssetChoose?: (val: string) => void;
}

const MultimediaDataGrid: FC<MultimediaDataGridProps> = ({ handleAssetChoose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const mainProgressContext = useMainProgressContext();
  const dataGridProgressContext = useDataGridProgressContext();
  const allMultimedia = useSelector(Selectors.allMultimedia);
  const allMultimediaState = useSelector(Selectors.apiAllMultimediaRequestState);
  const apiRequestState = useSelector(Selectors.apiMultimediaRequestState);
  const apiRequestError = useSelector(Selectors.apiMultimediaRequestError);
  const [open, setOpen] = useState(false);
  const [multimediaId, setMultimediaId] = useState('');

  const handleActionClick = useCallback((id: string) => {
    setOpen(true);
    setMultimediaId(id);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    dataGridProgressContext.show();
    dispatch(Actions.deleteMultimedia({ id: multimediaId }));
    setOpen(false);
  }, [multimediaId]);

  const handleDialogClose = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    dataGridProgressContext.show();
  }, []);

  useEffect(() => {
    dispatch(Actions.getAllMultimedia({}));
  }, []);

  useEffect(() => {
    mainProgressContext.hide();
  }, [allMultimedia]);

  useEffect(() => {
    if (allMultimediaState === API_REQUEST_STATE.SUCCEEDED_GET) {
      setTimeout(() => {
        dataGridProgressContext.hide();
      }, 500);
    }
  }, [allMultimediaState]);

  if (handleAssetChoose) {
    return (
      <div className="images-list">
        {allMultimedia.map((image) => (
          <div className="images-list__image-wrapper" onClick={() => handleAssetChoose(image.gcsUrl)} key={image.id}>
            <img src={image.gcsUrl} alt="gcs" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <DataGrid rows={allMultimedia}>
        <Column
          dataField="id"
          caption={t('multimedia.id')}
          width="10%"
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<MultimediaGetResponse>) => {
            return linkRender(cellData, cellData.data.id!);
          }}
        />
        <Column
          dataField="image"
          caption={t('multimedia.image')}
          width="10%"
          allowSorting={false}
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<MultimediaGetResponse>) => {
            return <DataGridImageTemplate imageSrc={cellData.data.gcsUrl} />;
          }}
        />
        <Column
          dataField="name"
          caption={t('multimedia.name')}
          width="30%"
          allowSorting={false}
          minWidth="300"
          cellRender={(cellData: DataGridCellRender<MultimediaGetResponse>) => {
            return linkRender(cellData, cellData.data.name);
          }}
        />
        <Column dataField="gcsUrl" caption={t('multimedia.url')} width="40%" allowSorting={false} minWidth="300" />
        <Column
          dataField="actions"
          caption=""
          width="10%"
          allowSorting={false}
          minWidth="100"
          cellRender={(cellData: DataGridCellRender<MultimediaGetResponse>) => {
            return <DataGridActionTemplate onClick={() => handleActionClick(cellData.data.id!)} />;
          }}
        />
      </DataGrid>
      <ActionConfirmationDialog
        open={open}
        onConfirm={handleDeleteConfirm}
        onClose={handleDialogClose}
        title={'multimedia.confirmation.confirmation'}
        text={'multimedia.confirmation.delete'}
      />

      <SnackbarSettings
        successMessage="multimedia.snackbar.delete_success"
        apiRequestState={apiRequestState}
        apiRequestError={apiRequestError}
        setApiRequestState={Actions.setApiMultimediaRequestState}
        onSuccess={() => {
          dispatch(Actions.getAllMultimedia({}));
        }}
        onError={() => {
          dataGridProgressContext.hide();
        }}
      />
    </>
  );
};

export default MultimediaDataGrid;
